<template>
  <div>
    <b-overlay
        :show="isLoading"
        rounded="sm"
    >

      <!-- Original -->
      <div v-if="originalData">
        <h4>Оригинал</h4>
        <b-card class="mb-2" border-variant="success">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h5 class="mb-0">{{ originalData.artist }} — {{ originalData.track }}</h5>
              <span>Прослушивания: {{ formatNumber(originalData.play_count) }}</span>
            </div>
            <div>
              <b-button
                  variant="info"
                  class="btn-icon"
                  :to="{name: 'track-detail', params: { id: originalData.id }, query: { tab: 'covers' }}"
              >
                <feather-icon icon="DiscIcon"/>
              </b-button>
              <b-button
                  variant="danger"
                  class="btn-icon ml-1"
                  @click="removeOriginal"
              >
                <feather-icon icon="XIcon"/>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Set original (when no original and no covers) -->
      <div v-if="trackData && !originalData && !(coversData && coversData.length)">
        <h4>Оригинал</h4>
        <b-card class="mb-2" border-variant="secondary">
          <div class="text-muted mb-1">Оригинал не задан</div>
          <div class="d-flex align-items-center">
            <b-form-input
                v-model="searchOriginalQuery"
                placeholder="Поиск трека для оригинала..."
                class="mr-1"
                @keyup.enter="searchForOriginal"
            />
            <b-button
                variant="outline-secondary"
                class="text-nowrap mr-1"
                @click="fillOriginalQuery"
                :disabled="!trackData || !trackData.track"
            >
              Заполнить
            </b-button>
            <b-button
                variant="primary"
                class="text-nowrap"
                @click="searchForOriginal"
                :disabled="!searchOriginalQuery.trim()"
            >
              <feather-icon icon="SearchIcon" class="mr-50"/>
              <span class="align-middle">Поиск</span>
            </b-button>
            <find-original-button
                v-if="trackData"
                :query="`${trackData.artist} - ${trackData.track}`"
                store-namespace="artists"
                class="ml-1"
            />
          </div>
          <div v-if="searchOriginalResults.length" class="mt-1">
            <h6>Результаты:</h6>
            <div
                v-for="result in searchOriginalResults"
                :key="result.id"
                class="d-flex align-items-center justify-content-between py-75 border-bottom"
            >
              <div class="d-flex align-items-center flex-wrap">
                <b-link
                    v-if="result.artist_id"
                    :to="{name: 'artist-view', params: { id: result.artist_id }}"
                    class="font-weight-bold"
                >{{ result.artist }}</b-link>
                <span v-else class="font-weight-bold">{{ result.artist }}</span>
                <span class="mx-25">—</span>
                <b-link
                    :to="{name: 'track-detail', params: { id: result.id }}"
                >{{ result.track }}</b-link>
                <b-badge v-if="result.is_cover" variant="warning" class="ml-50">кавер</b-badge>
                <b-badge v-if="result.has_covers" variant="success" class="ml-50">оригинал</b-badge>
                <small class="text-muted ml-50">[{{ formatNumber(result.play_count) }}]</small>
                <b-button
                    variant="flat-info"
                    class="btn-icon ml-50"
                    size="sm"
                    :href="googleCoverCheckUrl(result)"
                    target="_blank"
                    v-b-tooltip.hover
                    title="Проверить в Google"
                >
                  <feather-icon icon="GlobeIcon"/>
                </b-button>
              </div>
              <b-button
                  variant="success"
                  class="btn-icon ml-1"
                  size="sm"
                  @click="setOriginal(result.id)"
              >
                <feather-icon icon="CheckIcon"/>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Covers table -->
      <div v-if="coversData && !originalData">
        <h4 class="d-flex align-items-center">
          Каверы [{{ coversData.length }}]
          <b-button
              v-if="trackData"
              :href="googleSearchUrl()"
              variant="flat-info"
              class="btn-icon ml-50"
              size="sm"
              target="_blank"
          >
            <feather-icon icon="GlobeIcon"/>
          </b-button>
        </h4>
        <b-table
            :items="coversData"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="Нет каверов"
            class="position-relative"
        >

          <template #cell(play_count)="data">
            <span class="text-nowrap">
              {{ formatNumber(data.item.play_count) }}
            </span>
          </template>

          <template #cell(actions)="data">
            <span class="text-nowrap">
              <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="info"
                  class="btn-icon"
                  :to="{name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
              >
                <feather-icon icon="DiscIcon"/>
              </b-button>
              <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="warning"
                  class="btn-icon ml-1"
                  :to="{name: 'track-detail', params: { id: data.item.id }}"
              >
                <feather-icon icon="DownloadIcon"/>
              </b-button>
              <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="danger"
                  class="btn-icon ml-1"
                  @click="removeCover(data.item.id)"
              >
                <feather-icon icon="XIcon"/>
              </b-button>
            </span>
          </template>
        </b-table>
      </div>

      <!-- Add cover -->
      <div v-if="trackData && !originalData" class="mt-2">
        <h4>Добавить кавер</h4>
        <div class="d-flex align-items-center">
          <b-form-input
              v-model="searchCoverQuery"
              placeholder="Поиск трека для кавера..."
              class="mr-1"
              @keyup.enter="searchForCover"
          />
          <b-button
              variant="outline-secondary"
              class="text-nowrap mr-1"
              @click="fillCoverQuery"
              :disabled="!trackData || !trackData.track"
          >
            Заполнить
          </b-button>
          <b-button
              variant="primary"
              class="text-nowrap"
              @click="searchForCover"
              :disabled="!searchCoverQuery.trim()"
          >
            <feather-icon icon="SearchIcon" class="mr-50"/>
            <span class="align-middle">Поиск</span>
          </b-button>
          <find-original-button
              v-if="trackData"
              :query="`${trackData.artist} - ${trackData.track}`"
              store-namespace="artists"
              class="ml-1"
          />
        </div>
        <div v-if="searchCoverResults.length" class="mt-1">
          <h6>Результаты:</h6>
          <div
              v-for="result in searchCoverResults"
              :key="result.id"
              class="d-flex align-items-center justify-content-between py-75 border-bottom"
          >
            <div class="d-flex align-items-center flex-wrap">
              <b-link
                  v-if="result.artist_id"
                  :to="{name: 'artist-view', params: { id: result.artist_id }}"
                  class="font-weight-bold"
              >{{ result.artist }}</b-link>
              <span v-else class="font-weight-bold">{{ result.artist }}</span>
              <span class="mx-25">—</span>
              <b-link
                  :to="{name: 'track-detail', params: { id: result.id }}"
              >{{ result.track }}</b-link>
              <b-badge v-if="result.is_cover" variant="warning" class="ml-50">кавер</b-badge>
              <b-badge v-if="result.has_covers" variant="success" class="ml-50">оригинал</b-badge>
              <small class="text-muted ml-50">[{{ formatNumber(result.play_count) }}]</small>
              <b-button
                  variant="flat-info"
                  class="btn-icon ml-50"
                  size="sm"
                  :href="googleCoverCheckUrl(result)"
                  target="_blank"
                  v-b-tooltip.hover
                  title="Проверить в Google"
              >
                <feather-icon icon="GlobeIcon"/>
              </b-button>
            </div>
            <b-button
                variant="success"
                class="btn-icon ml-1"
                size="sm"
                @click="addCover(result.id)"
            >
              <feather-icon icon="PlusIcon"/>
            </b-button>
          </div>
        </div>
      </div>

      <!-- SHS Covers -->
      <div v-if="trackData && trackData.external_ids && trackData.external_ids.second_hand_songs != null" class="mt-2 shs-actions">
        <h4>
          SHS Каверы
          <b-button
              variant="outline-primary"
              size="sm"
              class="ml-1 shs-button"
              @click="fetchShsCovers"
          >
            <feather-icon icon="DownloadCloudIcon" class="mr-50"/>
            <span class="align-middle">Загрузить из SHS</span>
          </b-button>
        </h4>
        <div v-if="shsCoversError" class="text-danger mb-1">{{ shsCoversError }}</div>
        <b-table
            v-if="shsCovers.length"
            :items="shsCovers"
            responsive
            :fields="shsTableColumns"
            primary-key="shs_id"
            show-empty
            empty-text="Нет каверов"
            class="position-relative"
            :tbody-tr-class="shsRowClass"
        >
          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>

          <template #cell(performer)="data">
            <div class="d-flex align-items-center">
              <b-link
                  v-if="data.item.performer.id"
                  :to="{name: 'artist-view', params: { id: data.item.performer.id }}"
              >
                {{ data.item.performer.name }}
              </b-link>
              <span v-else>
                {{ data.item.performer.name }}
                <b-button
                    v-b-tooltip.hover
                    :title="'Найти артиста: ' + data.item.performer.name"
                    variant="outline-warning"
                    class="btn-icon ml-50"
                    size="sm"
                    :to="{name: 'artists-search', query: { q: data.item.performer.name }}"
                >
                  <feather-icon icon="SearchIcon"/>
                </b-button>
              </span>
              <b-button
                  v-if="data.item.performer.id && !data.item.track_id && !isCoverLinked(data.item.track_id)"
                  v-b-tooltip.hover
                  :title="'Добавить трек: ' + data.item.title"
                  variant="success"
                  class="btn-icon ml-50"
                  size="sm"
                  @click="addTrackAndCover(data.item)"
              >
                <feather-icon icon="PlusCircleIcon"/>
              </b-button>
            </div>
            <div
                v-if="shsVersionTitle(data.item) || shsVersionLanguage(data.item)"
                class="text-muted small"
            >
              <span v-if="shsVersionTitle(data.item)">{{ shsVersionTitle(data.item) }}</span>
              <b-badge
                  v-if="shsVersionLanguage(data.item)"
                  variant="light-info"
                  :class="{ 'ml-50': shsVersionTitle(data.item) }"
              >
                {{ shsVersionLanguage(data.item) }}
              </b-badge>
            </div>
          </template>

          <template #cell(is_original)="data">
            <b-badge :variant="data.item.is_original ? 'success' : 'warning'">
              {{ data.item.is_original ? 'Оригинал' : 'Кавер' }}
            </b-badge>
          </template>

          <template #cell(track_id)="data">
            <template v-if="data.item.track_id">
              <b-button
                  v-if="!isCurrentTrack(data.item.track_id)"
                  variant="primary"
                  class="btn-icon"
                  size="sm"
                  :to="{name: 'track-detail', params: { id: data.item.track_id }, query: { tab: 'covers' }}"
              >
                <feather-icon icon="LinkIcon"/>
              </b-button>
              <b-button
                  v-if="!isCurrentTrackCover && !isCoverLinked(data.item.track_id) && !isCurrentTrack(data.item.track_id) && !data.item.is_original"
                  variant="success"
                  class="btn-icon ml-50"
                  size="sm"
                  @click="addCover(data.item.track_id)"
              >
                <feather-icon icon="PlusIcon"/>
              </b-button>
              <b-button
                  v-if="data.item.is_original && !isCurrentTrack(data.item.track_id) && (!originalData || originalData.id !== data.item.track_id)"
                  v-b-tooltip.hover
                  title="Установить как оригинал"
                  variant="success"
                  class="btn-icon ml-50"
                  size="sm"
                  @click="setOriginal(data.item.track_id)"
              >
                <feather-icon icon="PlusIcon"/>
              </b-button>
              <b-badge
                  v-if="isCurrentTrack(data.item.track_id)"
                  variant="primary"
              >текущий</b-badge>
            </template>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(uri)="data">
            <b-button
                variant="flat-primary"
                class="btn-icon"
                size="sm"
                :href="data.item.uri"
                target="_blank"
            >
              <feather-icon icon="ExternalLinkIcon"/>
            </b-button>
          </template>
        </b-table>
      </div>

      <div v-if="!trackData && !isLoading" class="text-center text-muted py-2">
        Данные не найдены
      </div>

    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BOverlay,
  BTable,
  BFormInput,
  BBadge,
  BLink,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import {formatNumber} from '@core/utils/filter'
import router from "@/router"
import store from "@/store"
import {computed, ref, watch} from "@vue/composition-api"
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FindOriginalButton from './FindOriginalButton.vue'
import { createCoverLinkAfterSubmission, isSubmissionResponse, notifySubmitted } from '@/libs/moderation'

export default {
  components: {
    BCard,
    BButton,
    BOverlay,
    BTable,
    BFormInput,
    BBadge,
    BLink,
    FindOriginalButton,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  props: {
    trackId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props) {
    const toast = useToast()

    const trackData = ref(null)
    const originalData = ref(null)
    const coversData = ref(null)
    const isLoading = ref(false)

    const searchOriginalQuery = ref('')
    const searchOriginalResults = ref([])
    const searchCoverQuery = ref('')
    const searchCoverResults = ref([])
    const shsCovers = ref([])
    const shsCoversError = ref('')

    const tableColumns = [
      {key: 'id', label: '#', sortable: false},
      {key: 'artist', sortable: true},
      {key: 'track', sortable: true},
      {key: 'play_count', sortable: true},
      {key: 'actions'},
    ]

    const shsTableColumns = [
      { key: 'index', label: '#', sortable: false },
      { key: 'performer', label: 'Исполнитель', sortable: true },
      { key: 'is_original', label: 'Тип', sortable: true },
      { key: 'track_id', label: 'В БД', sortable: false },
      { key: 'uri', label: 'SHS', sortable: false },
    ]

    const shsLanguageNames = {
      English: 'английский',
      German: 'немецкий',
      French: 'французский',
      Spanish: 'испанский',
      Italian: 'итальянский',
      Portuguese: 'португальский',
      Russian: 'русский',
      Ukrainian: 'украинский',
      Belarusian: 'белорусский',
      Polish: 'польский',
      Czech: 'чешский',
      Slovak: 'словацкий',
      Hungarian: 'венгерский',
      Dutch: 'нидерландский',
      Swedish: 'шведский',
      Norwegian: 'норвежский',
      Danish: 'датский',
      Finnish: 'финский',
      Estonian: 'эстонский',
      Latvian: 'латышский',
      Lithuanian: 'литовский',
      Serbian: 'сербский',
      Croatian: 'хорватский',
      Bulgarian: 'болгарский',
      Romanian: 'румынский',
      Greek: 'греческий',
      Turkish: 'турецкий',
      Japanese: 'японский',
      Chinese: 'китайский',
      Korean: 'корейский',
      Hebrew: 'иврит',
      Instrumental: 'инструментал',
    }

    // Оригинал бэкенд всегда отдаёт первым — с ним сравниваем язык и название версий
    const shsOriginal = computed(() => shsCovers.value[0] || null)

    const shsVersionLanguage = (item) => {
      const originalLanguage = shsOriginal.value?.language
      if (!item.language || !originalLanguage || item.language === originalLanguage) return ''
      return shsLanguageNames[item.language] || item.language
    }

    const shsVersionTitle = (item) => {
      const originalTitle = shsOriginal.value?.title
      if (!item.title || !originalTitle || item.title === originalTitle) return ''
      return `«${item.title}»`
    }

    const isCoverLinked = (id) => {
      if (!id || !coversData.value) return false
      return coversData.value.some(c => c.id === id)
    }

    const isCurrentTrack = (id) => {
      return id && String(id) === String(props.trackId)
    }

    // Текущий трек — сам кавер (в БД задан оригинал или SHS пометил его кавером):
    // другие каверы цеплять к нему нельзя, только выставить оригинал
    const isCurrentTrackCover = computed(() => Boolean(originalData.value)
      || shsCovers.value.some(c => isCurrentTrack(c.track_id) && !c.is_original))

    const shsRowClass = (item) => {
      if (item && isCurrentTrack(item.track_id)) {
        return 'table-primary'
      }
      return ''
    }

    const fetchShsCovers = async () => {
      isLoading.value = true
      shsCoversError.value = ''
      try {
        const res = await store.dispatch('artists/fetchTrackShsCovers', {id: props.trackId})
        if (res.data.success) {
          shsCovers.value = res.data.covers
        } else {
          shsCoversError.value = res.data.message
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки SHS каверов',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const fetchCovers = async () => {
      isLoading.value = true
      try {
        const res = await store.dispatch('artists/fetchTrackCovers', {id: props.trackId})
        trackData.value = res.data.track
        originalData.value = res.data.original
        coversData.value = res.data.covers
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки каверов',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const searchForOriginal = async () => {
      const q = searchOriginalQuery.value.trim()
      if (!q) return
      isLoading.value = true
      try {
        const excludeArtistId = trackData.value ? trackData.value.artist_id : null
        const res = await store.dispatch('artists/searchTracksInDb', {q, exclude_id: excludeArtistId})
        searchOriginalResults.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const setOriginal = async (originalId) => {
      isLoading.value = true
      try {
        const response = await store.dispatch('artists/setTrackOriginal', {id: props.trackId, original_id: originalId})
        // Автор: правка ушла на одобрение — список не перезагружаем
        if (isSubmissionResponse(response)) {
          notifySubmitted(toast)
          return
        }
        searchOriginalQuery.value = ''
        searchOriginalResults.value = []
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал установлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка установки оригинала',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const removeOriginal = async () => {
      isLoading.value = true
      try {
        const response = await store.dispatch('artists/removeTrackOriginal', {id: props.trackId})
        if (isSubmissionResponse(response)) {
          notifySubmitted(toast)
          return
        }
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления оригинала',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const fillOriginalQuery = () => {
      if (trackData.value && trackData.value.track) {
        searchOriginalQuery.value = trackData.value.track
        searchForOriginal()
      }
    }

    const fillCoverQuery = () => {
      if (trackData.value && trackData.value.track) {
        searchCoverQuery.value = trackData.value.track
        searchForCover()
      }
    }

    const searchForCover = async () => {
      const q = searchCoverQuery.value.trim()
      if (!q) return
      isLoading.value = true
      try {
        const excludeArtistId = trackData.value ? trackData.value.artist_id : null
        const res = await store.dispatch('artists/searchTracksInDb', {q, exclude_id: excludeArtistId})
        searchCoverResults.value = res.data.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка поиска треков',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const addCover = async (coverId) => {
      isLoading.value = true
      try {
        const response = await store.dispatch('artists/addTrackCover', {id: props.trackId, cover_id: coverId})
        if (isSubmissionResponse(response)) {
          notifySubmitted(toast)
          return
        }
        searchCoverResults.value = searchCoverResults.value.filter(t => t.id !== coverId)
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const removeCover = async (coverId) => {
      isLoading.value = true
      try {
        const response = await store.dispatch('artists/removeTrackCover', {id: props.trackId, cover_id: coverId})
        if (isSubmissionResponse(response)) {
          notifySubmitted(toast)
          return
        }
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: 'Кавер удалён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка удаления кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const addTrackAndCover = async (shsItem) => {
      isLoading.value = true
      try {
        const res = await store.dispatch('artists/addTrack', {
          id: shsItem.performer.id,
          queryParams: { title: shsItem.title },
        })
        const shsOriginal = shsCovers.value.find(c => c.is_original && c.track_id)
        const parentId = shsOriginal ? shsOriginal.track_id : props.trackId
        // Автор: трек ушёл на одобрение — связь тоже заявкой, новым выступает заявка на трек
        if (isSubmissionResponse(res)) {
          notifySubmitted(toast)
          await createCoverLinkAfterSubmission(store, toast, shsItem.is_original
            ? {original_submission_id: res.data.submission.id, cover_track_id: props.trackId}
            : {original_track_id: parentId, cover_submission_id: res.data.submission.id})
          return
        }
        const newTrackId = res.data.track_id
        if (shsItem.is_original) {
          const originalResponse = await store.dispatch('artists/setTrackOriginal', {id: props.trackId, original_id: newTrackId})
          if (isSubmissionResponse(originalResponse)) {
            notifySubmitted(toast)
            return
          }
        } else {
          const coverResponse = await store.dispatch('artists/addTrackCover', {id: parentId, cover_id: newTrackId})
          if (isSubmissionResponse(coverResponse)) {
            notifySubmitted(toast)
            return
          }
        }
        shsItem.track_id = newTrackId
        await fetchCovers()
        toast({
          component: ToastificationContent,
          props: {
            title: shsItem.is_original ? 'Трек создан и установлен как оригинал' : 'Трек создан и добавлен как кавер',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека/кавера',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const googleSearchUrl = () => {
      if (!trackData.value) return '#'
      const query = `${trackData.value.artist} ${trackData.value.track} оригинал песни кавер чья песня`
      return `https://www.google.com/search?q=${encodeURIComponent(query)}`
    }

    const googleCoverCheckUrl = (result) => {
      const query = `${result.artist} ${result.track} оригинал песни кавер чья песня`
      return `https://www.google.com/search?q=${encodeURIComponent(query)}`
    }

    fetchCovers()

    return {
      googleSearchUrl,
      googleCoverCheckUrl,
      trackData,
      originalData,
      coversData,
      isLoading,
      tableColumns,
      formatNumber,
      searchOriginalQuery,
      searchOriginalResults,
      searchForOriginal,
      setOriginal,
      removeOriginal,
      searchCoverQuery,
      searchCoverResults,
      fillOriginalQuery,
      fillCoverQuery,
      searchForCover,
      addCover,
      removeCover,
      addTrackAndCover,
      isCoverLinked,
      shsCovers,
      shsCoversError,
      shsTableColumns,
      shsVersionLanguage,
      shsVersionTitle,
      fetchShsCovers,
      isCurrentTrack,
      isCurrentTrackCover,
      shsRowClass,
    }
  },
}
</script>
